import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';

// Define all possible channels
export enum IpcChannel {
  UPDATE_AVAILABLE = 'update-available',
  GET_VERSION = 'get-version',
  DOWNLOAD_IF_UPDATE_AVAILABLE = 'download-if-update-available',
  RESTART_APP = 'restart-app',
  GET_LOGS = 'get-logs',
  CLEAR_LOGS = 'clear-logs',
  DOWNLOAD_LOGS = 'download-logs',
  SAVE_USER = 'save-user',
  GET_USER = 'get-user',
  OPEN_URL = 'open-url',
  LIVE_LOG_ENTRY = 'live-log-entry',
  SAVE_FRONTEND_LOG = 'save-frontend-log',
  GET_AVAILABLE_LOG_DATES = 'get-available-log-dates',
  SHOW_CONTEXT_MENU = 'show-context-menu',
}

// Define message interfaces
export interface IpcMessage<T = any> {
  type: string;
  payload?: T;
}

export interface IpcResponse {
  data: any;
  error?: Error;
}

// Window interface
interface ElectronWindow extends Window {
  electron: {
    send: (channel: string, data: any) => void;
    on: (channel: string, callback: (data: any) => void) => void;
    removeListener: (channel: string) => void;
  };
}

@Injectable({
  providedIn: 'root',
})
export class IpcV2Service implements OnDestroy {
  private readonly window: ElectronWindow;
  private readonly listeners: Map<string, Subject<any>> = new Map();

  constructor() {
    this.window = window as unknown as ElectronWindow;
  }

  isElectron(): boolean {
    // Check for our specific electron bridge
    return !!this.window?.electron;
  }
  send(channel: IpcChannel, data: IpcMessage = null): void {
    if (!this.window.electron) {
      console.error('Electron IPC not available');
      return;
    }
    this.window.electron?.send(channel, data);
  }

  on<T>(channel: IpcChannel): Observable<T> {
    const channelKey = channel.toString(); // Add this line - channelKey was undefined!

    if (!this.listeners.has(channelKey)) {
      const subject = new Subject<T>();
      this.listeners.set(channelKey, subject);

      this.window.electron?.on(channelKey, (data: T) => {
        subject.next(data);
      });
    }
    return this.listeners.get(channelKey).asObservable();
  }

  sendAndReceive<T>(channel: IpcChannel, data?: any): Observable<IpcResponse> {
    return new Observable<IpcResponse>((observer) => {
      if (!this.window.electron) {
        observer.error(new Error('Electron IPC not available'));
        return () => {};
      }

      const skipLog = [
        IpcChannel.GET_LOGS,
        IpcChannel.GET_AVAILABLE_LOG_DATES,
        IpcChannel.DOWNLOAD_LOGS,
        IpcChannel.CLEAR_LOGS,
        IpcChannel.LIVE_LOG_ENTRY,
      ].includes(channel);

      if (!skipLog) {
        console.log(`IPC: ${channel}`, data);
      }

      // Setup listener for multiple responses
      const responseHandler = (response: unknown) => {
        if (!skipLog) {
          console.log(`Response: ${channel}`, response);
        }
        if (response != null) {
          observer.next(response as IpcResponse);

          // FIX: Check if this is a final response for certain channels
          if (this.isFinalResponse(channel, response as IpcResponse)) {
            observer.complete();
          }
        } else {
          observer.error(new Error('No response received'));
        }
      };

      this.window.electron.on(channel, responseHandler);
      this.window.electron.send(channel, data);

      return () => {
        this.window.electron.removeListener(channel);
      };
    });
  }
  private isFinalResponse(channel: IpcChannel, response: IpcResponse): boolean {
    // For download progress, only complete when status is final
    if (channel === IpcChannel.DOWNLOAD_IF_UPDATE_AVAILABLE) {
      const status = response.data?.status;
      return (
        status === 'completed' ||
        status === 'error' ||
        status === 'not-available'
      );
    }

    // For other channels, complete after first response
    return true;
  }

  removeListener(channel: IpcChannel): void {
    if (this.listeners.has(channel)) {
      this.listeners.get(channel)?.complete();
      this.listeners.delete(channel);
      this.window.electron.removeListener(channel);
    }
  }

  removeAllListeners(): void {
    this.listeners.forEach((_, channel) => {
      this.removeListener(channel as IpcChannel);
    });
  }

  ngOnDestroy(): void {
    this.removeAllListeners();
  }
}
